import React, { useCallback, useEffect, useState } from 'react'
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Icon,
    IconButton,
    Input,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverCloseButton,
    PopoverContent,
    PopoverFooter,
    PopoverHeader,
    PopoverTrigger,
    SimpleGrid,
    Text,
    Tooltip,
    useToast,
    VStack,
} from '@chakra-ui/react'
import { FaCheck, FaPalette, FaRotateLeft } from 'react-icons/fa6'

import MascotAvatar from './mascot-avatar'
import {
    MASCOT_COAT_SWATCHES,
    MASCOT_EAR_OPTIONS,
    MASCOT_EYE_SWATCHES,
    MASCOT_FUR_OPTIONS,
    MASCOT_NAME_MAX_CHARS,
    MASCOT_NICKNAME_MAX_CHARS,
    MASCOT_PATTERN_OPTIONS,
    MASCOT_SPECIES_OPTIONS,
    resolveMascotEarStyle,
    MASCOT_SNOUT_OPTIONS,
    resolveMascotPattern,
    resolveMascotSnout,
    sanitizeMascotName,
    sanitizeMascotNickname,
    type ClinicMascotIdentity,
    type MascotEarStyle,
    type MascotPattern,
} from './mascot-types'
import { useClinicMascot } from './use-clinic-mascot'

/**
 * Clinic mascot editor with an explicit save step.
 *
 * Edits accumulate in a local draft so the trigger / dock / audience
 * previews keep showing the saved mascot until Save is pressed. Saving
 * persists via `useClinicMascot` (which broadcasts to every other instance),
 * so all entry points refresh in the same turn.
 */
export default function MascotEditorButton() {
    const { mascot: savedMascot, setMascot, clinicId } = useClinicMascot()
    const [isOpen, setIsOpen] = useState(false)
    const [draft, setDraft] = useState<ClinicMascotIdentity>(savedMascot)
    const toast = useToast()

    // Seed the draft each time the popover opens, and keep it in sync when
    // the saved mascot changes underneath (e.g. another tab saved).
    useEffect(() => {
        if (isOpen) setDraft(savedMascot)
    }, [isOpen, savedMascot])

    const patchDraft = useCallback((patch: Partial<ClinicMascotIdentity>) => {
        setDraft(prev => ({ ...prev, ...patch }))
    }, [])

    if (!clinicId) return null

    const isDirty = JSON.stringify(draft) !== JSON.stringify(savedMascot)
    const nicknameError = !draft.nickname.trim()
        ? 'Nickname is required — it shows as “Ask {nickname}”.'
        : null
    const canSave = isDirty && !nicknameError

    const handleSave = () => {
        const nickname = sanitizeMascotNickname(draft.nickname)
        if (!nickname) return
        setMascot({
            ...draft,
            name: sanitizeMascotName(draft.name),
            nickname,
        })
        toast({
            title: 'Mascot saved',
            description: 'Updated everywhere Ask Chckvet appears.',
            status: 'success',
            duration: 2500,
            isClosable: true,
        })
        setIsOpen(false)
    }

    const handleReset = () => setDraft(savedMascot)

    return (
        <Popover
            placement="bottom-end"
            isLazy
            isOpen={isOpen}
            onOpen={() => {
                setDraft(savedMascot)
                setIsOpen(true)
            }}
            onClose={() => {
                setDraft(savedMascot)
                setIsOpen(false)
            }}
        >
            <PopoverTrigger>
                <Box display="inline-flex" flexShrink={0}>
                    <Button
                        display={{ base: 'none', lg: 'inline-flex' }}
                        aria-label="Customize clinic mascot"
                        leftIcon={<Icon as={FaPalette} />}
                        size="sm"
                        variant="outline"
                        rounded="full"
                        bg="white"
                    >
                        Customize
                    </Button>
                    <Tooltip label="Customize mascot" openDelay={400}>
                        <IconButton
                            display={{ base: 'inline-flex', lg: 'none' }}
                            aria-label="Customize clinic mascot"
                            icon={<Icon as={FaPalette} />}
                            size="sm"
                            variant="ghost"
                        />
                    </Tooltip>
                </Box>
            </PopoverTrigger>
            <PopoverContent
                w="320px"
                maxW="92vw"
                // There are more controls than fit on a laptop screen, so the
                // card is capped and the options scroll inside it.
                maxH="min(78vh, 620px)"
                display="flex"
                flexDirection="column"
                shadow="lg"
            >
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverHeader
                    fontWeight="semibold"
                    fontSize="sm"
                    flexShrink={0}
                >
                    Clinic mascot
                </PopoverHeader>
                {/* Pinned so you can see the mascot change while scrolling
                    the controls that change it. */}
                <Flex
                    justify="center"
                    py={3}
                    borderBottomWidth={1}
                    borderColor="gray.100"
                    flexShrink={0}
                >
                    <MascotAvatar mascot={draft} size={88} showCaption />
                </Flex>
                <PopoverBody
                    flex="1"
                    minH={0}
                    overflowY="auto"
                    overscrollBehavior="contain"
                    pb={4}
                >
                    <MascotEditorForm mascot={draft} onPatch={patchDraft} />
                </PopoverBody>
                <PopoverFooter
                    flexShrink={0}
                    borderTopWidth={1}
                    borderColor="gray.100"
                    px={3}
                    py={2.5}
                    bg="gray.50"
                    borderBottomRadius="md"
                >
                    <Flex align="center" gap={2}>
                        <Button
                            size="sm"
                            variant="ghost"
                            leftIcon={<Icon as={FaRotateLeft} />}
                            onClick={handleReset}
                            isDisabled={!isDirty}
                        >
                            Reset
                        </Button>
                        <Button
                            flex="1"
                            size="sm"
                            colorScheme="brand"
                            leftIcon={<Icon as={FaCheck} />}
                            onClick={handleSave}
                            isDisabled={!canSave}
                        >
                            Save mascot
                        </Button>
                    </Flex>
                    <Text
                        fontSize="xs"
                        color={
                            nicknameError
                                ? 'red.500'
                                : isDirty
                                  ? 'orange.600'
                                  : 'gray.500'
                        }
                        mt={1.5}
                        textAlign="center"
                    >
                        {nicknameError ??
                            (isDirty
                                ? 'You have unsaved changes'
                                : 'No unsaved changes')}
                    </Text>
                </PopoverFooter>
            </PopoverContent>
        </Popover>
    )
}

function MascotEditorForm({
    mascot,
    onPatch,
}: {
    mascot: ClinicMascotIdentity
    onPatch: (patch: Partial<ClinicMascotIdentity>) => void
}) {
    return (
        <VStack align="stretch" spacing={4}>
            <FormControl>
                <FormLabel fontSize="xs" mb={1}>
                    Full name
                </FormLabel>
                <Input
                    size="sm"
                    value={mascot.name}
                    maxLength={MASCOT_NAME_MAX_CHARS}
                    placeholder="e.g. Rocket"
                    onChange={e =>
                        onPatch({
                            name: e.target.value.slice(
                                0,
                                MASCOT_NAME_MAX_CHARS,
                            ),
                        })
                    }
                    onBlur={e =>
                        onPatch({ name: sanitizeMascotName(e.target.value) })
                    }
                />
            </FormControl>

            <FormControl isRequired isInvalid={!mascot.nickname.trim()}>
                <FormLabel fontSize="xs" mb={1}>
                    Nickname
                </FormLabel>
                <Input
                    size="sm"
                    value={mascot.nickname}
                    maxLength={MASCOT_NICKNAME_MAX_CHARS}
                    placeholder="Shown as Ask {nickname}"
                    onChange={e =>
                        onPatch({
                            nickname: e.target.value.slice(
                                0,
                                MASCOT_NICKNAME_MAX_CHARS,
                            ),
                        })
                    }
                    onBlur={e =>
                        onPatch({
                            nickname: sanitizeMascotNickname(e.target.value),
                        })
                    }
                />
                {!mascot.nickname.trim() ? (
                    <FormErrorMessage fontSize="xs" mt={1}>
                        Nickname is required — it shows as “Ask
                        {'{nickname}'}”.
                    </FormErrorMessage>
                ) : (
                    <FormHelperText fontSize="xs" mt={1}>
                        Max {MASCOT_NICKNAME_MAX_CHARS} characters. Shows as
                        “Ask {mascot.nickname.trim()}”.
                    </FormHelperText>
                )}
            </FormControl>

            <FormControl>
                <FormLabel fontSize="xs" mb={1.5}>
                    Species
                </FormLabel>
                <SimpleGrid columns={2} spacing={2}>
                    {MASCOT_SPECIES_OPTIONS.map(opt => {
                        const selected = mascot.species === opt.id
                        const preview: ClinicMascotIdentity = {
                            ...mascot,
                            species: opt.id,
                        }
                        return (
                            <Box
                                key={opt.id}
                                as="button"
                                type="button"
                                onClick={() =>
                                    onPatch({
                                        species: opt.id,
                                        // Keep the chosen ears/muzzle when the
                                        // new species offers them, else fall
                                        // back.
                                        earStyle: resolveMascotEarStyle(
                                            opt.id,
                                            mascot.earStyle,
                                        ),
                                        snout: resolveMascotSnout(
                                            opt.id,
                                            mascot.snout,
                                        ),
                                    })
                                }
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                gap={1.5}
                                p={2}
                                rounded="lg"
                                borderWidth={selected ? 2 : 1}
                                borderColor={
                                    selected ? 'brand.500' : 'gray.200'
                                }
                                bg={selected ? 'brand.50' : 'white'}
                                _hover={{ borderColor: 'brand.300' }}
                                transition="border-color 120ms ease, background 120ms ease"
                            >
                                <MascotAvatar mascot={preview} size={52} />
                                <Text
                                    fontSize="2xs"
                                    fontWeight="semibold"
                                    textAlign="center"
                                    lineHeight="1.2"
                                    noOfLines={2}
                                >
                                    {opt.label}
                                </Text>
                            </Box>
                        )
                    })}
                </SimpleGrid>
            </FormControl>

            <SnoutPicker mascot={mascot} onPatch={onPatch} />

            <EarPicker mascot={mascot} onPatch={onPatch} />

            <FormControl>
                <FormLabel fontSize="xs" mb={1.5}>
                    Coat length
                </FormLabel>
                <SimpleGrid columns={3} spacing={2}>
                    {MASCOT_FUR_OPTIONS.map(opt => {
                        const selected = mascot.furType === opt.id
                        const preview: ClinicMascotIdentity = {
                            ...mascot,
                            furType: opt.id,
                        }
                        return (
                            <Box
                                key={opt.id}
                                as="button"
                                type="button"
                                onClick={() => onPatch({ furType: opt.id })}
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                gap={1}
                                p={1.5}
                                rounded="lg"
                                borderWidth={selected ? 2 : 1}
                                borderColor={
                                    selected ? 'brand.500' : 'gray.200'
                                }
                                bg={selected ? 'brand.50' : 'white'}
                                _hover={{ borderColor: 'brand.300' }}
                                transition="border-color 120ms ease, background 120ms ease"
                            >
                                <MascotAvatar mascot={preview} size={44} />
                                <Text
                                    fontSize="2xs"
                                    fontWeight="semibold"
                                    textAlign="center"
                                    lineHeight="1.2"
                                    noOfLines={1}
                                >
                                    {opt.label}
                                </Text>
                            </Box>
                        )
                    })}
                </SimpleGrid>
            </FormControl>

            <CoatSwatchRow
                label="Primary coat"
                value={mascot.primaryColor}
                onChange={primaryColor => onPatch({ primaryColor })}
            />
            <CoatSwatchRow
                label="Secondary / markings"
                value={mascot.secondaryColor}
                onChange={secondaryColor => onPatch({ secondaryColor })}
            />
            <CoatSwatchRow
                label="Eyes"
                value={mascot.eyeColor}
                onChange={eyeColor => onPatch({ eyeColor })}
                swatches={MASCOT_EYE_SWATCHES}
            />

            <FormControl>
                <FormLabel fontSize="xs" mb={1.5}>
                    Markings
                </FormLabel>
                {/* Shown as previews rather than chips — "Blaze" vs "Mask" vs
                    "Bicolor" is hard to picture from the label alone. */}
                <SimpleGrid columns={3} spacing={2}>
                    {MASCOT_PATTERN_OPTIONS[mascot.species].map(opt => {
                        const selected =
                            resolveMascotPattern(
                                mascot.species,
                                mascot.pattern,
                            ) === opt.id
                        const preview: ClinicMascotIdentity = {
                            ...mascot,
                            pattern: opt.id,
                        }
                        return (
                            <Box
                                key={opt.id}
                                as="button"
                                type="button"
                                onClick={() =>
                                    onPatch({
                                        pattern: opt.id as MascotPattern,
                                    })
                                }
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                gap={1}
                                p={1.5}
                                rounded="lg"
                                borderWidth={selected ? 2 : 1}
                                borderColor={
                                    selected ? 'brand.500' : 'gray.200'
                                }
                                bg={selected ? 'brand.50' : 'white'}
                                _hover={{ borderColor: 'brand.300' }}
                                transition="border-color 120ms ease, background 120ms ease"
                            >
                                <MascotAvatar mascot={preview} size={44} />
                                <Text
                                    fontSize="2xs"
                                    fontWeight="semibold"
                                    textAlign="center"
                                    lineHeight="1.2"
                                    noOfLines={1}
                                >
                                    {opt.label}
                                </Text>
                            </Box>
                        )
                    })}
                </SimpleGrid>
            </FormControl>
        </VStack>
    )
}

/** Hidden for species with only one sensible ear shape (e.g. the horse). */
/** Hidden for species that only come one way — a cat has one muzzle. */
function SnoutPicker({
    mascot,
    onPatch,
}: {
    mascot: ClinicMascotIdentity
    onPatch: (patch: Partial<ClinicMascotIdentity>) => void
}) {
    const options = MASCOT_SNOUT_OPTIONS[mascot.species]
    if (options.length < 2) return null

    const current = resolveMascotSnout(mascot.species, mascot.snout)

    return (
        <FormControl>
            <FormLabel fontSize="xs" mb={1.5}>
                Muzzle
            </FormLabel>
            <SimpleGrid columns={3} spacing={2}>
                {options.map(opt => {
                    const selected = current === opt.id
                    const preview: ClinicMascotIdentity = {
                        ...mascot,
                        snout: opt.id,
                    }
                    return (
                        <Box
                            key={opt.id}
                            as="button"
                            type="button"
                            onClick={() => onPatch({ snout: opt.id })}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            gap={1}
                            p={1.5}
                            rounded="lg"
                            borderWidth={selected ? 2 : 1}
                            borderColor={selected ? 'brand.500' : 'gray.200'}
                            bg={selected ? 'brand.50' : 'white'}
                            _hover={{ borderColor: 'brand.300' }}
                            transition="border-color 120ms ease, background 120ms ease"
                        >
                            <MascotAvatar mascot={preview} size={44} />
                            <Text
                                fontSize="2xs"
                                fontWeight="semibold"
                                textAlign="center"
                                lineHeight="1.2"
                                noOfLines={1}
                            >
                                {opt.label}
                            </Text>
                        </Box>
                    )
                })}
            </SimpleGrid>
        </FormControl>
    )
}

function EarPicker({
    mascot,
    onPatch,
}: {
    mascot: ClinicMascotIdentity
    onPatch: (patch: Partial<ClinicMascotIdentity>) => void
}) {
    const options = MASCOT_EAR_OPTIONS[mascot.species]
    if (options.length < 2) return null

    const current = resolveMascotEarStyle(mascot.species, mascot.earStyle)

    return (
        <FormControl>
            <FormLabel fontSize="xs" mb={1.5}>
                Ears
            </FormLabel>
            <SimpleGrid columns={3} spacing={2}>
                {options.map(opt => {
                    const selected = current === opt.id
                    const preview: ClinicMascotIdentity = {
                        ...mascot,
                        earStyle: opt.id,
                    }
                    return (
                        <Box
                            key={opt.id}
                            as="button"
                            type="button"
                            onClick={() =>
                                onPatch({
                                    earStyle: opt.id as MascotEarStyle,
                                })
                            }
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            gap={1}
                            p={1.5}
                            rounded="lg"
                            borderWidth={selected ? 2 : 1}
                            borderColor={selected ? 'brand.500' : 'gray.200'}
                            bg={selected ? 'brand.50' : 'white'}
                            _hover={{ borderColor: 'brand.300' }}
                            transition="border-color 120ms ease, background 120ms ease"
                        >
                            <MascotAvatar mascot={preview} size={44} />
                            <Text
                                fontSize="2xs"
                                fontWeight="semibold"
                                textAlign="center"
                                lineHeight="1.2"
                                noOfLines={1}
                            >
                                {opt.label}
                            </Text>
                        </Box>
                    )
                })}
            </SimpleGrid>
        </FormControl>
    )
}

function CoatSwatchRow({
    label,
    value,
    onChange,
    swatches = MASCOT_COAT_SWATCHES,
}: {
    label: string
    value: string
    onChange: (hex: string) => void
    /** Presets to offer. Defaults to the coat palette. */
    swatches?: readonly string[]
}) {
    return (
        <FormControl>
            <FormLabel fontSize="xs" mb={1.5}>
                {label}
            </FormLabel>
            <Flex gap={1.5} wrap="wrap" align="center">
                {swatches.map(hex => {
                    const selected = hex.toLowerCase() === value.toLowerCase()
                    return (
                        <Box
                            key={`${label}-${hex}`}
                            as="button"
                            type="button"
                            aria-label={`${label} ${hex}`}
                            w="22px"
                            h="22px"
                            rounded="full"
                            bg={hex}
                            borderWidth={selected ? 2 : 1}
                            borderColor={selected ? 'brand.500' : 'gray.300'}
                            boxShadow={
                                hex.toLowerCase() === '#ffffff'
                                    ? 'inset 0 0 0 1px rgba(0,0,0,0.08)'
                                    : undefined
                            }
                            onClick={() => onChange(hex)}
                        />
                    )
                })}
                <Input
                    type="color"
                    value={normalizeColorInput(value)}
                    onChange={e => onChange(e.target.value.toUpperCase())}
                    w="36px"
                    h="28px"
                    p={0}
                    border="none"
                    cursor="pointer"
                    title="Custom color"
                />
            </Flex>
        </FormControl>
    )
}

/** `<input type="color">` needs #RRGGBB. */
function normalizeColorInput(hex: string): string {
    if (/^#[0-9a-fA-F]{6}$/.test(hex)) return hex
    if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
        const [, r, g, b] = hex
        return `#${r}${r}${g}${g}${b}${b}`
    }
    return '#FFFBF5'
}
